import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Container, Typography, Grid, Box, Button, useTheme } from "@mui/material";
import HikingIcon from "@mui/icons-material/Hiking";
import PoolIcon from "@mui/icons-material/Pool";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WineBarIcon from "@mui/icons-material/WineBar";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import Reveal from "../components/Reveal";
import { FullBleed, PageHero, Section, SectionTitle, ZoomImage } from "../components/layout";
import CONTACT_INFO from "../constants/contactInfo";

const IMAGES = {
  hero: "https://ik.imagekit.io/cabanasdelcampo/alrededores/20241027_133414.jpg?updatedAt=1737325675800",
  nombre:
    "https://ik.imagekit.io/cabanasdelcampo/exteriores/WhatsApp%20Image%202026-06-17%20at%2011.38.30%20(1).jpeg?updatedAt=1781707373234",
  paisaje:
    "https://ik.imagekit.io/cabanasdelcampo/exteriores/WhatsApp%20Image%202026-06-17%20at%2011.38.32%20(1).jpeg?updatedAt=1781707373471",
};

const DATOS = [
  { value: "+1.100 m", label: "sobre el nivel del mar" },
  { value: "2 ríos", label: "Los Reartes y San Miguel" },
  { value: "16 km", label: "de Villa General Belgrano" },
];

const ACTIVIDADES = [
  {
    icon: HikingIcon,
    titulo: "Caminatas y bici",
    texto: "Caminos de ripio ideales para caminar o pedalear, con vistas a las Sierras Grandes y al valle.",
  },
  {
    icon: PoolIcon,
    titulo: "Ríos y arroyos",
    texto: "En verano, chapuzones en los ríos y arroyos de la zona, y balnearios como Puente Blanco.",
  },
  {
    icon: VisibilityOutlinedIcon,
    titulo: "Observación de fauna",
    texto: "Aves y animales silvestres en estado natural, en un entorno de bosque serrano.",
  },
  {
    icon: WineBarIcon,
    titulo: "Vinos de altura",
    texto: "Visitas guiadas al viñedo y la bodega de Finca Atos, con degustación.",
  },
  {
    icon: LocalFloristIcon,
    titulo: "Lavanda",
    texto: "Productores de lavanda y destiladores de aceites esenciales en los alrededores.",
  },
  {
    icon: TwoWheelerIcon,
    titulo: "Cuatriciclos",
    texto: "Paseos guiados por miradores y caminos serranos, desde nuestras cabañas.",
    link: "/experiencias",
  },
  {
    icon: PhotoCameraOutlinedIcon,
    titulo: "Fotografía",
    texto: "Lomadas, arboledas y cerros que cambian de color según la hora del día.",
  },
];

const FAUNA = ["Zorros", "Liebres", "Armadillos", "Perdices", "Zorrinos", "Halcones peregrinos"];

const ALREDEDORES = [
  {
    nombre: "Villa General Belgrano",
    distancia: "16 km",
    texto: "Capital Nacional de la Cerveza, con su arquitectura centroeuropea y la Oktoberfest.",
  },
  {
    nombre: "La Cumbrecita",
    distancia: "15 km",
    texto: "Pueblo peatonal de montaña, con senderos, cascadas y bosques.",
  },
  {
    nombre: "Los Reartes",
    texto: "Uno de los pueblos más antiguos del valle, a orillas del río del mismo nombre.",
  },
  {
    nombre: "Cristo Grande",
    texto: "Mirador panorámico sobre el camino que une Athos Pampa con Villa General Belgrano.",
  },
  {
    nombre: "Balneario Puente Blanco",
    texto: "A pocos minutos por el camino de ripio hacia Yacanto, ideal para el verano.",
  },
  {
    nombre: "Cerro Champaquí",
    texto: "El cerro más alto de Córdoba, que se recorta en el horizonte de las Sierras Grandes.",
  },
];

const AthosPampa = () => {
  const theme = useTheme();
  const green = theme.palette.secondary.dark;

  return (
    <Box>
      <PageHero image={IMAGES.hero} overline="Nuestro lugar" title="Athos Pampa">
        La pampa del zorro, una pampa de altura en el corazón del Valle de Calamuchita.
      </PageHero>

      {/* El nombre */}
      <Section>
        <Container>
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Reveal>
                <SectionTitle overline="Historia y significado" title="La pampa del zorro" sx={{ mb: 4 }} />
                <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
                  El nombre "Athos Pampa" —también escrito Atos Pampa— significa
                  "pampa de zorro". Es una localidad antigua del Valle de
                  Calamuchita, de población dispersa, sin un centro urbano: acá
                  el paisaje lo marcan las fincas, los campos abiertos y los
                  caminos de tierra.
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: "text.secondary" }}>
                  Se extiende a más de 1.100 metros sobre el nivel del mar, entre
                  los ríos Los Reartes y San Miguel, en el camino hacia las Sierras
                  Grandes. Un lugar tranquilo, con su escuela y su capilla, ideal
                  para desconectarse.
                </Typography>
              </Reveal>
              <Reveal delay={150}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    mt: 4,
                    pt: 4,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {DATOS.map((dato) => (
                    <Box key={dato.label}>
                      <Typography
                        sx={{
                          fontFamily: theme.typography.h1.fontFamily,
                          fontSize: { xs: "1.6rem", md: "2.2rem" },
                          fontWeight: 600,
                          color: "secondary.main",
                          lineHeight: 1.1,
                        }}
                      >
                        {dato.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
                        {dato.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "100%" }}>
              <Reveal delay={100}>
                <ZoomImage sx={{ height: { xs: 320, md: 540 }, "& img": { objectPosition: "center 75%" } }}>
                  <img src={IMAGES.nombre} alt="Paisaje de Athos Pampa" loading="lazy" />
                </ZoomImage>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Paisaje */}
      <FullBleed
        sx={{
          position: "relative",
          height: { xs: "55vh", md: "70vh" },
          backgroundImage: `url("${IMAGES.paisaje}")`,
          backgroundSize: "cover",
          backgroundPosition: "center 15%",
          display: "flex",
          alignItems: "flex-end",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0) 60%)",
          },
        }}
      >
        <Container sx={{ position: "relative", pb: { xs: 5, md: 8 } }}>
          <Reveal>
            <Typography
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontStyle: "italic",
                color: "white",
                fontSize: { xs: "1.7rem", md: "2.8rem" },
                maxWidth: 760,
                lineHeight: 1.2,
              }}
            >
              Suaves lomadas, arboledas y arroyos, con las Sierras Grandes en el horizonte.
            </Typography>
          </Reveal>
        </Container>
      </FullBleed>

      {/* Qué hacer */}
      <Section>
        <Container>
          <Reveal>
            <SectionTitle overline="Qué hacer" title="Naturaleza a cada paso">
              Athos Pampa invita a bajar el ritmo y a disfrutar del aire libre.
            </SectionTitle>
          </Reveal>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              columnGap: 5,
            }}
          >
            {ACTIVIDADES.map(({ icon: Icon, titulo, texto, link }, index) => (
              <Reveal key={titulo} delay={(index % 3) * 80}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    py: 3,
                    borderBottom: `1px solid ${theme.palette.sand.dark}`,
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "rgba(203,110,40,0.12)",
                      color: "primary.main",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: "1.1rem", mb: 0.5 }}>
                      {titulo}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {texto}
                    </Typography>
                    {link && (
                      <Button
                        component={RouterLink}
                        to={link}
                        size="small"
                        endIcon={<ArrowOutwardIcon fontSize="small" />}
                        sx={{ mt: 1, px: 0, "&:hover": { backgroundColor: "transparent", transform: "none", boxShadow: "none" } }}
                      >
                        Ver experiencia
                      </Button>
                    )}
                  </Box>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Section>

      {/* Flora y fauna */}
      <Section sx={{ backgroundColor: theme.palette.sand.light, py: { xs: 8, md: 10 } }}>
        <Container>
          <Grid container spacing={{ xs: 4, md: 10 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Reveal>
                <SectionTitle overline="Flora y fauna" title="Un bosque serrano vivo" sx={{ mb: 0 }} />
              </Reveal>
            </Grid>
            <Grid item xs={12} md={7}>
              <Reveal delay={100}>
                <Typography variant="body1" paragraph sx={{ color: "text.secondary" }}>
                  La vegetación es la del bosque chaqueño serrano. En las zonas
                  rurales es común cruzarse con fauna autóctona, y en las partes
                  más altas de las sierras habitan pumas y, con suerte, se ven
                  cóndores.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                  {FAUNA.map((animal) => (
                    <Box
                      key={animal}
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: 999,
                        backgroundColor: "background.paper",
                        border: `1px solid ${theme.palette.sand.dark}`,
                        fontSize: "0.9rem",
                        fontWeight: 500,
                      }}
                    >
                      {animal}
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Producción local */}
      <Section sx={{ backgroundColor: green }}>
        <Container>
          <Reveal>
            <SectionTitle overline="Sabores de la zona" title="Vino y lavanda de altura" light>
              La amplitud térmica de la zona —días de sol intenso y noches
              frescas— crea un microclima especial para estos cultivos.
            </SectionTitle>
          </Reveal>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Reveal sx={{ height: "100%" }}>
                <Box
                  sx={{
                    height: "100%",
                    p: { xs: 3, md: 5 },
                    borderRadius: "24px",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <WineBarIcon sx={{ color: "primary.light", fontSize: 36, mb: 2 }} />
                  <Typography variant="h4" sx={{ color: "white", mb: 2 }}>
                    Finca Atos
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", mb: 2 }}>
                    Viñedo y bodega familiar a unos 1.200 msnm. Empezó en 2002 con
                    una hectárea experimental y siete variedades —Chardonnay,
                    Sauvignon Blanc, Malbec, Syrah, Merlot, Cabernet Sauvignon y
                    Pinot Noir— y hoy tiene siete hectáreas trabajadas con
                    agricultura natural.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", mb: 3 }}>
                    Ofrecen visitas guiadas por el viñedo y la bodega, con cierre
                    en la sala de degustación.
                  </Typography>
                  <Button
                    variant="outlined"
                    href="https://fincaatos.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<ArrowOutwardIcon />}
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.6)",
                      "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    Conocer Finca Atos
                  </Button>
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={6}>
              <Reveal delay={120} sx={{ height: "100%" }}>
                <Box
                  sx={{
                    height: "100%",
                    p: { xs: 3, md: 5 },
                    borderRadius: "24px",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <LocalFloristIcon sx={{ color: "primary.light", fontSize: 36, mb: 2 }} />
                  <Typography variant="h4" sx={{ color: "white", mb: 2 }}>
                    Campos de lavanda
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.85)" }}>
                    El clima y la geografía de Athos Pampa permitieron el cultivo
                    de lavanda. Hoy hay pequeños y medianos productores y
                    destiladores de aceites esenciales, que convierten a la zona en
                    un paseo aromático para descubrir durante tu estadía.
                  </Typography>
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Alrededores */}
      <Section>
        <Container>
          <Reveal>
            <SectionTitle overline="Alrededores" title="Para explorar el valle">
              Desde las cabañas, los principales destinos del Valle de
              Calamuchita quedan a pocos kilómetros.
            </SectionTitle>
          </Reveal>
          <Grid container spacing={2}>
            {ALREDEDORES.map((lugar, index) => (
              <Grid item xs={12} sm={6} md={4} key={lugar.nombre}>
                <Reveal delay={(index % 3) * 80} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      height: "100%",
                      p: 3,
                      borderRadius: "20px",
                      backgroundColor: "background.paper",
                      border: `1px solid ${theme.palette.sand.dark}`,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: "1.15rem" }}>
                        {lugar.nombre}
                      </Typography>
                      {lugar.distancia && (
                        <Typography
                          variant="body2"
                          sx={{ color: "primary.main", fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          {lugar.distancia}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {lugar.texto}
                    </Typography>
                  </Box>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* Cierre */}
      <Section sx={{ backgroundColor: theme.palette.sand.light, mb: "-20px", textAlign: "center" }}>
        <Container maxWidth="md">
          <Reveal>
            <SectionTitle overline="Te esperamos" title="Vení a conocer Athos Pampa" align="center">
              Descansá en nuestras cabañas y descubrí uno de los rincones más
              tranquilos de las sierras de Córdoba.
            </SectionTitle>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                href={CONTACT_INFO.reservarLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Consultar disponibilidad
              </Button>
              <Button variant="outlined" size="large" component={RouterLink} to="/como-llegar">
                Cómo llegar
              </Button>
            </Box>
          </Reveal>
        </Container>
      </Section>
    </Box>
  );
};

export default AthosPampa;
